import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { SlRequestCloseEvent } from '@shoelace-style/shoelace/cdn/events/sl-request-close';

import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon';
import type SlAlertElement from '@shoelace-style/shoelace/dist/components/alert/alert';

import {
  useCohortThreads, useCourseDisplayNames, useDebounce, useLearningPathConfig,
} from '../../hooks';
import type { AllowedCourseItem, CohortThread } from '../../types';

import ThreadRow from './ThreadRow';
import messages from './messages';
import { courseLabel } from './utils';

interface CohortDiscussionsProps {
  slug: string;
  language: string;
}

interface ModerationNotification {
  success: boolean;
  msg: string;
}

/** Moderation panel for the Discussions tab; role gating lives in the caller. */
const CohortDiscussions: React.FC<CohortDiscussionsProps> = ({ slug, language }) => {
  const intl = useIntl();
  const { config, loaded: configLoaded } = useLearningPathConfig();
  const courses: AllowedCourseItem[] = config?.[language]?.allowed_courses ?? [];
  const displayNames = useCourseDisplayNames(language);

  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // `/cohorts/:slug` doesn't remount on navigating between cohorts, so a course
  // id selected for the previous language can linger. Validate it synchronously:
  // an effect-based reset would let one render pass the new slug with the stale
  // courseId to `useCohortThreads`, fetching a combination never selected.
  const selectedCourse = courses.find((course) => course.course_id === selectedCourseId);
  const effectiveCourseId = selectedCourse ? selectedCourseId : null;

  useEffect(() => {
    if (!effectiveCourseId && courses.length > 0) {
      setSelectedCourseId(courses[0].course_id);
    }
  }, [courses, effectiveCourseId]);

  const {
    threads, loaded, errorStatus, hasMore, isLoadingMore, loadMore, deleteThread,
  } = useCohortThreads(slug, effectiveCourseId);

  const [pendingDelete, setPendingDelete] = useState<CohortThread | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [notification, setNotification] = useState<ModerationNotification | null>(null);
  const notificationRef = useRef<SlAlertElement | null>(null);

  useDebounce(() => {
    if (notification?.msg && notificationRef.current && 'toast' in notificationRef.current) {
      notificationRef.current?.toast();
    }
  }, 200, [notification]);

  // An accidental outside click must not silently drop a pending delete.
  const handleRequestClose = (event: SlRequestCloseEvent): void => {
    if (event.detail.source === 'overlay') {
      event.preventDefault();
    }
  };

  const handleConfirmDelete = (): void => {
    if (!pendingDelete) {
      return;
    }
    setIsDeleting(true);

    deleteThread(
      pendingDelete.id,
      () => {
        setIsDeleting(false);
        setPendingDelete(null);
        setNotification({ success: true, msg: intl.formatMessage(messages.discussionsDeleteSuccessToast) });
      },
      () => {
        setIsDeleting(false);
        setPendingDelete(null);
        setNotification({ success: false, msg: intl.formatMessage(messages.discussionsDeleteErrorToast) });
      },
    );
  };

  return (
    <div className="cohort-discussions">
      <SlAlert
        ref={notificationRef}
        variant={notification?.success ? 'success' : 'danger'}
        duration={5000}
        closable
      >
        <SlIcon slot="icon" name={notification?.success ? 'check2-circle' : 'exclamation-octagon'} />
        <strong>{notification?.msg}</strong>
      </SlAlert>

      {configLoaded && courses.length === 0 && (
        <p className="leg-p">{intl.formatMessage(messages.discussionsNoCoursesMessage)}</p>
      )}

      {configLoaded && courses.length > 0 && (
        <>
          <div className="form-row cohort-discussions-course-select">
            <label htmlFor="cohort-discussions-course">
              {intl.formatMessage(messages.discussionsModuleSelectLabel)}
            </label>
            <select
              id="cohort-discussions-course"
              value={effectiveCourseId ?? ''}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              {courses.map((course) => (
                <option key={course.course_id} value={course.course_id}>
                  {courseLabel(course, displayNames.get(course.course_id))}
                </option>
              ))}
            </select>
          </div>

          {!loaded && (
            <p className="leg-p">{intl.formatMessage(messages.discussionsThreadsLoading)}</p>
          )}

          {loaded && errorStatus !== null && (
            <p className="form-error form-error-custom">{intl.formatMessage(messages.discussionsThreadsLoadError)}</p>
          )}

          {/* Pages can remain while the loaded page is empty (right after
              deleting its last row), so the empty state gates on `!hasMore`. */}
          {loaded && errorStatus === null && threads.length === 0 && !hasMore && (
            <p className="leg-p">{intl.formatMessage(messages.discussionsThreadsEmpty)}</p>
          )}

          {loaded && errorStatus === null && (threads.length > 0 || hasMore) && (
            <>
              {threads.length > 0 && (
                <ul className="cohort-discussions-list">
                  {threads.map((thread) => (
                    <ThreadRow
                      key={thread.id}
                      thread={thread}
                      onDeleteClick={() => setPendingDelete(thread)}
                    />
                  ))}
                </ul>
              )}
              {hasMore && (
                <div className="cohort-detail-load-more">
                  <button
                    type="button"
                    className="cohort-detail-load-more-button"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {intl.formatMessage(messages.loadMoreButton)}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <SlDialog
        className="cohort-discussions-delete-dialog"
        label={intl.formatMessage(messages.discussionsDeleteConfirmTitle)}
        open={!!pendingDelete}
        onSlRequestClose={handleRequestClose}
        onSlAfterHide={() => setPendingDelete(null)}
      >
        <p className="leg-p">{intl.formatMessage(messages.discussionsDeleteConfirmBody)}</p>
        <SlButton slot="footer" onClick={() => setPendingDelete(null)}>
          {intl.formatMessage(messages.discussionsDeleteCancelButton)}
        </SlButton>
        <SlButton slot="footer" variant="danger" loading={isDeleting} onClick={handleConfirmDelete}>
          {intl.formatMessage(messages.discussionsDeleteConfirmButton)}
        </SlButton>
      </SlDialog>
    </div>
  );
};

export default CohortDiscussions;
